"use client";

import { ReactElement, useState } from "react";
import { Transition } from "@headlessui/react";
import {
  XMarkIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";
import { useNotifications } from "../../stateDefs";

export function NotificationsContainer() {
  const notifications = useNotifications((state) => state.notifications);

  return (
    <>
      <div
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6 z-50"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          {notifications.map((notification) => (
            <NotificationBox
              key={notification.id}
              notification={notification}
            />
          ))}
        </div>
      </div>
    </>
  );
}

export type Notification = {
  id: string;
  type?: "info" | "warning" | "error" | "success";
  title?: string;
  content: ReactElement | string;
};

type NotificationBoxProps = {
  notification: Notification;
};

export function NotificationBox(props: NotificationBoxProps) {
  const [show, setShow] = useState(true);

  return (
    <Transition
      show={show}
      enter="transform transition ease-out duration-300"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 transition data-closed:opacity-0 data-enter:transform data-enter:duration-300 data-enter:ease-out data-closed:data-enter:translate-y-2 data-leave:duration-100 data-leave:ease-in data-closed:data-enter:sm:translate-x-2 data-closed:data-enter:sm:translate-y-0">
        <div className="p-4">
          <div className="flex items-start">
            <div className="shrink-0">
              {props.notification.type === "info" && (
                <InformationCircleIcon
                  aria-hidden="true"
                  className="size-6 fill-gray-500"
                />
              )}
              {props.notification.type === "warning" && (
                <InformationCircleIcon
                  aria-hidden="true"
                  className="size-6 fill-yellow-500"
                />
              )}
              {props.notification.type === "error" && (
                <InformationCircleIcon
                  aria-hidden="true"
                  className="size-6 fill-red-500"
                />
              )}
              {props.notification.type === "success" && (
                <CheckCircleIcon
                  aria-hidden="true"
                  className="size-6 fill-green-500"
                />
              )}
            </div>
            <div className="ml-3 w-0 flex-1 pt-0.5">
              <p className="text-sm font-medium text-gray-900">
                {props.notification.title}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {props.notification.content}
              </p>
            </div>
            <div className="ml-4 flex shrink-0">
              <button
                type="button"
                onClick={() => {
                  setShow(false);
                }}
                className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
}
